import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Grid,
  Typography,
  Divider,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  RadioGroup,
  FormControlLabel,
  TextField,
  Chip,
  Paper,
  IconButton,
  LinearProgress,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { toast } from "react-toastify";
import FeatherIcon from "feather-icons-react";
import CustomRadio from "../../../components/forms/custom-elements/CustomRadio";
import { 
  ApproveEmployeeOfTheMonth, 
  GetEmployeeOfTheMonthMPCVoteList, 
  GetNomineebyId,
  GetExtraOrdinaryAboutNominee 
} from "../../../services/human-resource";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "1100px",
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  maxHeight: "90vh",
  overflow: "hidden",
};

const EmployeeOfMonthVoteModal = ({
  open,
  handleClose,
  nomineeId,
  canVote,
  onVoteSubmitted,
}) => {
  const [loading, setLoading] = useState(false);
  const [vote, setVote] = useState("");
  const [remarks, setRemarks] = useState("");
  const [nominee, setNominee] = useState({});
  const [committeeVotes, setCommitteeVotes] = useState([]);
  const [extraordinaryQuestions, setExtraordinaryQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [hasAlreadyVoted, setHasAlreadyVoted] = useState(false);

  useEffect(() => {
    if (open && nomineeId) {
      setVote("");
      setRemarks("");
      setNominee({});
      setCommitteeVotes([]);
      setHasAlreadyVoted(false);

      loadNominee();
      loadCommitteeVotes();
      loadExtraordinaryQuestions();
    }

    if (!open) {
      setVote("");
      setRemarks("");
      setHasAlreadyVoted(false);
    }
  }, [open, nomineeId]);

  // Lock the voting controls as soon as the current user's existing vote
  // is available from either the vote-list response or MPCVotesJson.
  useEffect(() => {
    if (!open || !nomineeId || !nominee) {
      return;
    }

    if (!Array.isArray(committeeVotes)) {
      return;
    }

    setHasAlreadyVoted(
      detectCurrentUserVote(nominee, committeeVotes)
    );
  }, [open, nomineeId, nominee, committeeVotes]);

  // Load selected questions when nominee data and extraordinary questions are available
  useEffect(() => {
    if (nominee.nominatedDetailsId && extraordinaryQuestions.length > 0) {
      matchAndSetSelectedQuestions();
    }
  }, [nominee.nominatedDetailsId, extraordinaryQuestions]);


  const readStoredUser = () => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  };

  const getStoredIdentity = () => {
    const storedUser = readStoredUser();
    const legacyPersonId = localStorage.getItem("userDetail");

    return {
      personId:
        storedUser?.PersonId ??
        storedUser?.personId ??
        storedUser?.personID ??
        legacyPersonId ??
        null,
      userId:
        storedUser?.UserId ??
        storedUser?.userId ??
        storedUser?.userid ??
        storedUser?.id ??
        null,
      userName:
        storedUser?.UserName ??
        storedUser?.username ??
        storedUser?.name ??
        "",
    };
  };

  const isVoteRecorded = (row) => {
    if (!row) return false;

    if (
      row.hasVoted === true ||
      row.HasVoted === true
    ) {
      return true;
    }

    const voteValue =
      row.vote ??
      row.Vote ??
      row.decision ??
      row.Decision ??
      null;

    if (
      voteValue !== null &&
      voteValue !== undefined &&
      String(voteValue).trim() !== ""
    ) {
      const normalizedVote = String(voteValue).trim().toLowerCase();

      if (
        normalizedVote === "1" ||
        normalizedVote === "2" ||
        normalizedVote === "approve" ||
        normalizedVote === "approved" ||
        normalizedVote === "reject" ||
        normalizedVote === "rejected"
      ) {
        return true;
      }
    }

    const approved = row.approved ?? row.Approved;
    const denied = row.denied ?? row.Denied;

    return (
      approved === true ||
      approved === 1 ||
      String(approved).toLowerCase() === "true" ||
      String(approved) === "1" ||
      denied === true ||
      denied === 1 ||
      String(denied).toLowerCase() === "true" ||
      String(denied) === "1"
    );
  };

  const detectCurrentUserVote = (nomineeData, votes) => {
    const { personId, userId, userName } = getStoredIdentity();

    const personIdCandidates = [
      personId,
      nomineeData?.personId,
      nomineeData?.PersonId,
    ]
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map((value) => String(value));

    const userIdCandidates = [
      userId,
      nomineeData?.userId,
      nomineeData?.UserId,
    ]
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map((value) => String(value));

    // Preferred: use an ID exposed by the vote-list response.
    const matchedVoteRow = (Array.isArray(votes) ? votes : []).find((row) => {
      const rowPersonIds = [
        row?.personId,
        row?.PersonId,
        row?.personID,
        row?.PersonID,
      ]
        .filter((value) => value !== null && value !== undefined && value !== "")
        .map((value) => String(value));

      const rowUserIds = [
        row?.userId,
        row?.UserId,
        row?.userid,
        row?.UserID,
      ]
        .filter((value) => value !== null && value !== undefined && value !== "")
        .map((value) => String(value));

      const samePerson =
        personIdCandidates.length > 0 &&
        rowPersonIds.some((value) => personIdCandidates.includes(value));

      const sameUser =
        userIdCandidates.length > 0 &&
        rowUserIds.some((value) => userIdCandidates.includes(value));

      if (samePerson || sameUser) {
        return isVoteRecorded(row);
      }

      // Name is only a fallback when the API does not expose identity fields.
      if (
        userName &&
        (row?.personName || row?.PersonName) &&
        String(row.personName ?? row.PersonName).trim().toLowerCase() ===
          String(userName).trim().toLowerCase()
      ) {
        return isVoteRecorded(row);
      }

      return false;
    });

    if (matchedVoteRow) {
      return true;
    }

    // Second: MPCVotesJson uses UserMaster.Id as the JSON key.
    const rawVotesJson =
      nomineeData?.mpcVotesJson ??
      nomineeData?.MPCVotesJson ??
      "";

    if (!rawVotesJson) {
      return false;
    }

    let votesJson;
    try {
      votesJson =
        typeof rawVotesJson === "string"
          ? JSON.parse(rawVotesJson)
          : rawVotesJson;
    } catch {
      return false;
    }

    const nomineeMpcUserIds = String(
      nomineeData?.mpcUserIds ??
      nomineeData?.MPCUserIds ??
      ""
    )
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);

    const nomineeMpcPersonIds = String(
      nomineeData?.mpcPersonIds ??
      nomineeData?.MPCPersonIds ??
      ""
    )
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);

    let currentUserMasterId = userIdCandidates[0] || "";

    // Map logged-in PersonId -> UserMaster.Id when both dynamic MPC lists exist.
    if (!currentUserMasterId && personIdCandidates.length > 0) {
      const personIndex = nomineeMpcPersonIds.findIndex((value) =>
        personIdCandidates.includes(value)
      );

      if (
        personIndex >= 0 &&
        personIndex < nomineeMpcUserIds.length
      ) {
        currentUserMasterId = nomineeMpcUserIds[personIndex];
      }
    }

    if (!currentUserMasterId) {
      return false;
    }

    const memberVote = votesJson?.[currentUserMasterId];

    return isVoteRecorded({
      vote: memberVote?.vote,
      approved: Number(memberVote?.vote) === 1,
      denied: Number(memberVote?.vote) === 2,
    });
  };

  const loadNominee = async () => {
    try {
      setLoading(true);
      const response = await GetNomineebyId(nomineeId);
      if (response.status === 200) {
        setNominee(response.data[0]);
      }
    } catch {
      toast.error("Unable to load nominee.");
    } finally {
      setLoading(false);
    }
  };

  const loadCommitteeVotes = async () => {
    try {
      const response = await GetEmployeeOfTheMonthMPCVoteList(nomineeId);
      if (response.status === 200) {
        setCommitteeVotes(response.data);
      }
    } catch (error) {
      toast.error("Unable to load committee votes.");
    }
  };

  const loadExtraordinaryQuestions = async () => {
    try {
      setLoadingQuestions(true);
      const response = await GetExtraOrdinaryAboutNominee();
      if (response.status === 200) {
        setExtraordinaryQuestions(response.data);
      }
    } catch (error) {
      console.error("Error loading extraordinary questions:", error);
      toast.error("Unable to load extraordinary questions.");
    } finally {
      setLoadingQuestions(false);
    }
  };

  const matchAndSetSelectedQuestions = () => {
    if (!nominee.nominatedDetailsId || !extraordinaryQuestions.length) {
      setSelectedQuestions([]);
      return;
    }

    // Parse the comma-separated IDs
    const questionIds = nominee.nominatedDetailsId
      .split(',')
      .map(id => id.trim())
      .filter(id => id !== '');

    // Filter extraordinary questions that match the IDs
    const matchedQuestions = extraordinaryQuestions.filter(q => 
      questionIds.includes(String(q.id)) || questionIds.includes(String(q.questionId))
    );

    setSelectedQuestions(matchedQuestions);
  };

  const submitVote = async () => {
    if (hasAlreadyVoted) {
      toast.info("You have already voted for this nominee.");
      return;
    }

    if (!vote) {
      toast.error("Please select your vote.");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        nomineeId,
        isApproved: vote === "Approve",
        remarks,
         approvalType: "MPC",
      };
      const response = await ApproveEmployeeOfTheMonth(payload);
      if (response.status === 200) {
        toast.success("Vote submitted successfully.");

        // Refresh modal data so the just-submitted vote is immediately
        // recognised and the controls remain locked on reopen.
        await Promise.all([loadCommitteeVotes(), loadNominee()]);

        // Refresh the parent MPC pending list. After a successful vote the
        // nominee should no longer be in this member's pending queue.
        if (typeof onVoteSubmitted === "function") {
          await onVoteSubmitted();
        }

        handleClose();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Unable to submit vote."
      );
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map(word => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getVoteProgress = () => {
    const total = committeeVotes.length;
    if (total === 0) return 0;
    const approved = committeeVotes.filter(v => v.approved).length;
    return Math.round((approved / total) * 100);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        {/* Header */}
        <Box
          sx={{
            px: 4,
            py: 3,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "background.default",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <Box display="flex" alignItems="center">
            <Avatar
              sx={{
                bgcolor: "primary.main",
                width: 40,
                height: 40,
                mr: 2,
              }}
            >
              <FeatherIcon icon="award" size={20} style={{ color: "#fff" }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                Employee of the Month
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Committee Voting Process
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: "text.secondary" }}>
            <FeatherIcon icon="x" size={22} />
          </IconButton>
        </Box>

        {loading && <LinearProgress sx={{ height: 2 }} />}

        <Box sx={{ p: 4 }}>
          <Grid container spacing={4}>
            {/* Left Panel */}
            <Grid item xs={12} md={7}>
              <Box>
                <Typography
                  variant="overline"
                  color="primary"
                  sx={{ fontWeight: 600, letterSpacing: 1 }}
                >
                  Nominee Information
                </Typography>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {nominee.firstName} {nominee.middleName} {nominee.lastName}
                </Typography>

                <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Department
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {nominee.departmentName || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Supervisor
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {nominee.supervisor || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Month
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {nominee.month ? new Date(0, nominee.month - 1).toLocaleString('default', { month: 'long' }) : "-"}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2.5 }} />

                {/* Selected Questions - Extraordinary Qualities */}
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Extraordinary Qualities
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: "background.default",
                    borderRadius: 2,
                    scrollbarWidth: "thin",
                    borderColor: "divider",
                    maxHeight: 200,
                    overflow: "auto",
                  }}
                >
                  {loadingQuestions ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <LinearProgress sx={{ width: "100%" }} />
                    </Box>
                  ) : selectedQuestions.length > 0 ? (
                    <List dense disablePadding>
                      {selectedQuestions.map((question, index) => (
                        <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <FeatherIcon 
                              icon="check-circle" 
                              size={16} 
                              style={{ color: "#1976d2" }} 
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={question.questionText || question.description || question.name || `Quality ${index + 1}`}
                            primaryTypographyProps={{
                              variant: "body2",
                              sx: { fontWeight: 500 }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center" py={1}>
                      No extraordinary qualities selected
                    </Typography>
                  )}
                </Paper>

                {/* Additional Details */}
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Additional Details
                </Typography>
                <Paper
  variant="outlined"
  sx={{
    p: 2.5,
    mb: 3,
    bgcolor: "background.default",
    borderRadius: 2,
    borderColor: "divider",
    maxHeight: 200,
    overflowY: "auto",
    scrollbarWidth: "thin",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  }}
>
  <Typography variant="body2" color="text.primary">
    {nominee.additionalDetails || "No additional details provided."}
  </Typography>
</Paper>

               
              </Box>
            </Grid>

            {/* Right Panel */}
            <Grid item xs={12} md={5}>
              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Voting Status
                  </Typography>
                  <Chip
                    label={`${committeeVotes.length} members`}
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                </Box>

                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    borderColor: "divider",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "grey.50" }}>
                        <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Role</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, py: 1.5 }}>
                          ✓
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, py: 1.5 }}>
                          ✗
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {committeeVotes.map((row, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            "&:hover": { bgcolor: "action.hover" },
                            transition: "background 0.2s",
                          }}
                        >
                          <TableCell sx={{ py: 1.5 }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Avatar
                                sx={{
                                  width: 28,
                                  height: 28,
                                  bgcolor: "primary.light",
                                  color: "primary.main",
                                  fontSize: "0.65rem",
                                  fontWeight: 600,
                                }}
                              >
                                {getInitials(row.personName)}
                              </Avatar>
                              <Typography variant="body2" fontWeight={500}>
                                {row.personName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Chip
                              label={row.roleName}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 22,
                                fontSize: "0.6rem",
                                fontWeight: 500,
                                borderRadius: 1,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1.5 }}>
                            {row.approved ? (
                              <FeatherIcon icon="check-circle" size={18} color="#2e7d32" />
                            ) : (
                              <Typography variant="body2" color="grey.400">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1.5 }}>
                            {row.denied ? (
                              <FeatherIcon icon="x-circle" size={18} color="#d32f2f" />
                            ) : (
                              <Typography variant="body2" color="grey.400">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {committeeVotes.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <FeatherIcon icon="users" size={32} style={{ color: "#ccc" }} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              No votes recorded yet
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>
                 <Divider sx={{ my: 2.5 }} />

                {/* Voting Section */}
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Your Vote
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  {hasAlreadyVoted
                    ? "You have already voted for this nominee"
                    : canVote
                      ? "Cast your vote for this nominee"
                      : "You are not authorized to vote"}
                </Typography>

                {hasAlreadyVoted && (
                  <Alert
                    severity="success"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  >
                    You have already submitted your vote for this nominee.
                    Voting, remarks, and Submit Vote are now locked.
                  </Alert>
                )}

                <RadioGroup
                  row
                  value={vote}
                  onChange={(e) => setVote(e.target.value)}
                  sx={{ mb: 2.5 }}
                >
                  <FormControlLabel
                    value="Approve"
                    control={<CustomRadio />}
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <FeatherIcon icon="thumbs-up" size={14} style={{ color: "#2e7d32" }} />
                        <span>Approve</span>
                      </Box>
                    }
                    disabled={!canVote || hasAlreadyVoted}
                  />
                  <FormControlLabel
                    value="Reject"
                    control={<CustomRadio />}
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <FeatherIcon icon="thumbs-down" size={14} style={{ color: "#d32f2f" }} />
                        <span>Reject</span>
                      </Box>
                    }
                    disabled={!canVote || hasAlreadyVoted}
                  />
                </RadioGroup>

                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  value={remarks}
                  disabled={!canVote || hasAlreadyVoted || loading}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Add your remarks..."
                  variant="outlined"
                  size="medium"
                  sx={{ mb: 3 }}
                />

                {/* Actions */}
                <Box display="flex" gap={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={submitVote}
                    disabled={!canVote || hasAlreadyVoted || loading || !vote}
                    startIcon={<FeatherIcon icon="check-circle" size={18} />}
                    sx={{
                      px: 4,
                      py: 1.2,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Submit Vote
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    sx={{
                      px: 3,
                      py: 1.2,
                      borderRadius: 2,
                      textTransform: "none",
                    }}
                  >
                    Close
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

      </Box>
    </Modal>
  );
};

export default EmployeeOfMonthVoteModal;